import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

const tagsPrivateAdapter = createEntityAdapter({
  selectId: (tag) => tag.id,
});

const tagsPublicAdapter = createEntityAdapter({
  selectId: (tag) => tag.id,
});

const tagsPendingAdapter = createEntityAdapter({
  selectId: (tag) => tag.id,
});

const initialState = {
  tagsPrivate: tagsPrivateAdapter.getInitialState(),
  tagsPublic: tagsPublicAdapter.getInitialState(),
  tagsPending: tagsPendingAdapter.getInitialState(),
  status: 'idle',
  error: null,
};

// Async thunks
export const fetchTags = createAsyncThunk(
  'tags/fetchTags',
  async ({ namespace }, { extra }) => {
    const { tagsApi } = extra;
    return tagsApi.getTags(namespace);
  },
);

export const createTag = createAsyncThunk(
  'tags/createTag',
  async ({ tag, namespace }, { extra }) => {
    const { tagsApi } = extra;
    return tagsApi.createTag(tag, namespace);
  },
);

export const renameTag = createAsyncThunk(
  'tags/renameTag',
  async ({ tagId, name }, { extra }) => {
    const { tagsApi } = extra;
    return tagsApi.updateTag(tagId, { name });
  },
);

export const mergeTags = createAsyncThunk(
  'tags/mergeTags',
  async ({ targetTagId, sourceTagIds }, { extra }) => {
    const { tagsApi } = extra;
    const updated = await tagsApi.mergeTags(targetTagId, sourceTagIds);
    return { updated, sourceTagIds };
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTags.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        const tags = action.payload;
        if (tags.privateData) {
          tagsPrivateAdapter.setAll(state.tagsPrivate, tags.privateData);
        }
        if (tags.publicData) {
          tagsPublicAdapter.setAll(state.tagsPublic, tags.publicData);
        }
        if (tags.pendingData) {
          tagsPendingAdapter.setAll(state.tagsPending, tags.pendingData);
        }
        state.status = 'succeeded';
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createTag.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createTag.fulfilled, (state, action) => {
        if (action.meta.arg.namespace === 'public') {
          tagsPublicAdapter.upsertOne(state.tagsPublic, action.payload);
        } else {
          tagsPrivateAdapter.upsertOne(state.tagsPrivate, action.payload);
        }
        state.status = 'succeeded';
      })
      .addCase(createTag.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(renameTag.fulfilled, (state, action) => {
        tagsPrivateAdapter.upsertOne(state.tagsPrivate, action.payload);
      })
      .addCase(mergeTags.fulfilled, (state, action) => {
        const { updated, sourceTagIds } = action.payload;
        tagsPrivateAdapter.removeMany(state.tagsPrivate, sourceTagIds);
        if (updated && updated.id) {
          tagsPrivateAdapter.upsertOne(state.tagsPrivate, updated);
        }
      });
  },
});

export default tagsSlice.reducer;

export const {
  selectAll: selectAllTagsPrivate,
} = tagsPrivateAdapter.getSelectors((state) => state.tags.tagsPrivate);

export const {
  selectAll: selectAllTagsPublic,
} = tagsPublicAdapter.getSelectors((state) => state.tags.tagsPublic);

export const {
  selectAll: selectAllTagsPending,
} = tagsPendingAdapter.getSelectors((state) => state.tags.tagsPending);

const normalizeTagName = (name) => (typeof name === 'string' ? name.trim().toLowerCase() : '');

/**
 * The user's own tags plus the public ones, each carrying `isPublic`.
 */
export const selectTagVocabulary = createSelector(
  [selectAllTagsPrivate, selectAllTagsPublic],
  (privateTags, publicTags) => {
    const publicNames = new Set(publicTags.map((tag) => normalizeTagName(tag.name)));
    const publicIds = new Set(publicTags.map((tag) => tag.id));

    const vocabulary = privateTags.map((tag) => ({
      ...tag,
      isPublic: publicIds.has(tag.id) || publicNames.has(normalizeTagName(tag.name)),
    }));

    const privateNames = new Set(privateTags.map((tag) => normalizeTagName(tag.name)));
    const privateIds = new Set(privateTags.map((tag) => tag.id));
    publicTags.forEach((tag) => {
      if (privateIds.has(tag.id) || privateNames.has(normalizeTagName(tag.name))) return;
      vocabulary.push({ ...tag, isPublic: true });
    });

    return vocabulary;
  },
);

/**
 * The client twin of the server tag resolution: the public tags referred by
 * private tag by id or name.
 */
export const selectResolveShareTags = createSelector(
  [selectAllTagsPrivate, selectAllTagsPublic],
  (privateTags, publicTags) => {
    const publicIds = new Set(publicTags.map((tag) => tag.id));
    const publicIdByName = new Map(
      publicTags.map((tag) => [normalizeTagName(tag.name), tag.id]),
    );
    const nameById = new Map(privateTags.map((tag) => [tag.id, normalizeTagName(tag.name)]));

    return (tagIds) => {
      const publicTagIds = [];
      if (!tagIds || !tagIds.length) return publicTagIds;

      const seen = new Set();
      const keep = (id) => {
        if (seen.has(id)) return;
        seen.add(id);
        publicTagIds.push(id);
      };

      // A public id stays, else the name decides.
      tagIds.forEach((tagId) => {
        if (publicIds.has(tagId)) {
          keep(tagId);
          return;
        }
        const publicId = publicIdByName.get(nameById.get(tagId));
        if (publicId) keep(publicId);
      });

      return publicTagIds;
    };
  },
);

/**
 * A predicate for spotting non-public tags. Flagging pending tags to not survive
 * approval. To be used by moderators.
 */
export const selectIsProposedTag = createSelector(
  [selectAllTagsPublic],
  (publicTags) => {
    if (publicTags.length === 0) return () => false;
    const publicIds = new Set(publicTags.map((tag) => tag.id));
    const publicNames = new Set(publicTags.map((tag) => normalizeTagName(tag.name)));
    return (tag) => Boolean(tag)
      && !publicIds.has(tag.id)
      && !publicNames.has(normalizeTagName(tag.name));
  },
);
